class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = null;
    this.slides = [];
    this.timeout = null;
    this.isRolling = false;

    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  connectedCallback() {
    this.announcementBar = this.querySelector('#wt-announcement__container');
    this.slides = Array.from(this.announcementBar?.children || []);
    this.isRolling = this.dataset.rollingEnabled === 'true' && this.slides.length > 1;

    if (this.announcementBar) {
      this.announcementBar.style.transition = `transform ${this.getTransitionSeconds()}s ease-in-out`;
    }

    if (this.isRolling) {
      this.showSlide(0, false);
      this.scheduleNext();

      this.announcementBar.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.announcementBar.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      this.announcementBar.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    this.closeButton = this.querySelector('.wt-announcement__close');
    if (this.closeButton) this.closeButton.addEventListener('click', this.hideForOneDay);

    if (Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }

  getTransitionSeconds() {
    // Keep compatibility with Wonder's existing animation_speed saved values.
    const speed = Number(this.dataset.transitionSpeed || 100);
    if (speed >= 400) return 0.65;
    if (speed >= 150) return 0.5;
    if (speed >= 100) return 0.4;
    return 0.3;
  }

  getCurrentDelay() {
    const slide = this.slides[this.currentIndex];
    const seconds = Number(slide?.dataset.displayDuration || 5);
    return Math.max(2, seconds) * 1000;
  }

  scheduleNext() {
    clearTimeout(this.timeout);
    if (!this.isRolling) return;
    this.timeout = setTimeout(() => this.nextAnnouncement(), this.getCurrentDelay());
  }

  showSlide(index, animate = true) {
    if (!this.announcementBar || !this.slides.length) return;

    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.announcementBar.style.transition = animate
      ? `transform ${this.getTransitionSeconds()}s ease-in-out`
      : 'none';
    this.announcementBar.style.transform = `translateX(-${this.currentIndex * 100}%)`;

    if (!animate) {
      requestAnimationFrame(() => {
        if (this.announcementBar) {
          this.announcementBar.style.transition = `transform ${this.getTransitionSeconds()}s ease-in-out`;
        }
      });
    }
  }

  nextAnnouncement() {
    this.showSlide(this.currentIndex + 1);
    this.scheduleNext();
  }

  previousAnnouncement() {
    this.showSlide(this.currentIndex - 1);
    this.scheduleNext();
  }

  handleTouchStart(event) {
    clearTimeout(this.timeout);
    this.touchStartX = event.touches[0].clientX;
    this.touchEndX = this.touchStartX;
  }

  handleTouchMove(event) {
    this.touchEndX = event.touches[0].clientX;
  }

  handleTouchEnd() {
    const difference = this.touchEndX - this.touchStartX;
    const swipeThreshold = 30;

    if (Math.abs(difference) > swipeThreshold) {
      difference < 0 ? this.nextAnnouncement() : this.previousAnnouncement();
    } else {
      this.scheduleNext();
    }
  }

  handleBlockSelect(event) {
    const selectedSlide = event.target?.closest?.('.wt-announcement__body');
    if (!selectedSlide || !this.contains(selectedSlide)) return;

    const index = this.slides.indexOf(selectedSlide);
    if (index < 0) return;

    clearTimeout(this.timeout);
    this.showSlide(index);
    this.scheduleNext();
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem('wt-announcement-hidden', oneDayLater);
    this.style.display = 'none';
  }

  disconnectedCallback() {
    clearTimeout(this.timeout);

    if (this.announcementBar) {
      this.announcementBar.removeEventListener('touchstart', this.handleTouchStart);
      this.announcementBar.removeEventListener('touchmove', this.handleTouchMove);
      this.announcementBar.removeEventListener('touchend', this.handleTouchEnd);
    }

    if (this.closeButton) this.closeButton.removeEventListener('click', this.hideForOneDay);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
